import React, {useState, useEffect, useRef} from "react";
import Swal from "sweetalert2";
import {
    agregarPuntoVenta,
    actualizarPuntoVenta,
    obtenerArchivos,
    subirArchivo,
    eliminarArchivo,
} from "../AccionesBackend";
import {Button} from "react-bootstrap";

const FormularioPuntoVenta = ({puntoVentaSeleccionado, onGuardar, onCancelar}) => {
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        imgModulo_id: null,
        latitud: "",
        longitud: "",
        direccion: "",
        estado: "",
        localidad: "",
        tipo_mueble: "",
        estado_mueble: "",
        television: "",
        tv_funcional: false,
        imagen_viniles: "",
        conexion_luz: false,
        cuenta_staff: false,
        comentarios: "",
    });

    // Estado para almacenar la URL de la imagen actual (si existe)
    const [imgUrl, setImgUrl] = useState("");

    // Referencia para el input file (oculto)
    const fileInputRef = useRef(null);
    const [nuevoArchivo, setNuevoArchivo] = useState(null); // Archivo seleccionado

    useEffect(() => {
        if (puntoVentaSeleccionado) {
            setFormData(puntoVentaSeleccionado);
            // Si ya tiene imagen asociada, intentamos obtener la ruta del archivo:
            if (puntoVentaSeleccionado.imgModulo_id) {
                obtenerRutaArchivo(puntoVentaSeleccionado.imgModulo_id);
            } else {
                setImgUrl("");
            }
        } else {
            // Formulario en blanco
            setFormData({
                title: "",
                description: "",
                imgModulo_id: null,
                latitud: "",
                longitud: "",
                direccion: "",
                estado: "",
                localidad: "",
                tipo_mueble: "",
                estado_mueble: "",
                television: "",
                tv_funcional: false,
                imagen_viniles: "",
                conexion_luz: false,
                cuenta_staff: false,
                comentarios: "",
            });
            setImgUrl("");
        }
    }, [puntoVentaSeleccionado]);

    // Función para convertir un TINYINT(1) a boolean
    const boolValue = (val) => val === 1 || val === true;

    // ========================
    // Obtener la ruta del archivo a partir de su ID
    // ========================
    const obtenerRutaArchivo = async (archivoId) => {
        try {
            const response = await obtenerArchivos({archivoId});
            if (response.success && response.archivos.length > 0) {
                setImgUrl(response.archivos[0].ruta_archivo);
            } else {
                setImgUrl("");
            }
        } catch (err) {
            console.error(err);
            setImgUrl("");
        }
    };

    // Manejar cambios en los inputs
    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    // Guardar o actualizar y luego mostrar SweetAlert2 para notificar el éxito, refrescando la lista
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Preparamos los datos a enviar (con booleans convertidos a 0/1 si es necesario)
        const envio = {
            ...formData,
            tv_funcional: formData.tv_funcional ? 1 : 0,
            conexion_luz: formData.conexion_luz ? 1 : 0,
            cuenta_staff: formData.cuenta_staff ? 1 : 0,
        };

        if (!puntoVentaSeleccionado) {
            // === CREAR NUEVO PUNTO DE VENTA ===
            const respAdd = await agregarPuntoVenta(envio);
            if (!respAdd.success) {
                Swal.fire({icon: "error", title: "Error", text: "Error al crear el punto de venta"});
                return;
            }
            const newId = respAdd.id;
            if (nuevoArchivo) {
                const respArchivo = await subirArchivo({
                    archivo: nuevoArchivo,
                    usuarioId: 1, // o el ID real de tu usuario
                    seccionId: 20, // asumiendo que 20 es la sección "Puntos de Venta"
                });
                if (!respArchivo.success) {
                    Swal.fire({icon: "error", title: "Error", text: "Error al subir el archivo"});
                    return;
                }
                const respUpdate = await actualizarPuntoVenta(newId, {
                    ...envio,
                    imgModulo_id: respArchivo.archivo.id,
                });
                if (!respUpdate.success) {
                    Swal.fire({
                        icon: "error",
                        title: "Error",
                        text: "Error al asignar la imagen al nuevo punto de venta"
                    });
                    return;
                }
            }
            Swal.fire({
                icon: "success",
                title: "¡LISTO!",
                text: "Se guardaron los cambios",
                timer: 2000,
                showConfirmButton: false,
            }).then(() => {
                onGuardar(); // Llama a la función que cierra el formulario y refresca la lista
            });
        } else {
            // === ACTUALIZAR PUNTO DE VENTA EXISTENTE ===
            const respUpdate = await actualizarPuntoVenta(puntoVentaSeleccionado.id, envio);
            if (!respUpdate.success) {
                Swal.fire({icon: "error", title: "Error", text: "Error al actualizar el punto de venta"});
                return;
            }
            if (nuevoArchivo) {
                const respArchivo = await subirArchivo({
                    archivo: nuevoArchivo,
                    usuarioId: 1,
                    seccionId: 20,
                });
                if (!respArchivo.success) {
                    Swal.fire({icon: "error", title: "Error", text: "Error al subir el archivo"});
                    return;
                }
                if (formData.imgModulo_id) {
                    await eliminarArchivo({archivoId: formData.imgModulo_id});
                }
                const respUpdateImg = await actualizarPuntoVenta(puntoVentaSeleccionado.id, {
                    ...envio,
                    imgModulo_id: respArchivo.archivo.id,
                });
                if (!respUpdateImg.success) {
                    Swal.fire({icon: "error", title: "Error", text: "Error al asignar la imagen nueva"});
                    return;
                }
            }
            Swal.fire({
                icon: "success",
                title: "¡LISTO!",
                text: "Se guardaron los cambios",
                timer: 2000,
                showConfirmButton: false,
            }).then(() => {
                onGuardar(); // Llama a la función que refresca la lista y cierra el formulario
            });
        }
    };

    // ========================
    // Acciones relacionadas con la imagen
    // ========================
    const handleVerImagen = () => {
        if (imgUrl) {
            window.open(imgUrl, "_blank");
        } else {
            alert("No hay imagen para mostrar");
        }
    };

    const handleActualizarImagen = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setNuevoArchivo(file);
    };

    const handleCoordenadasChange = (e) => {
        const { name, value } = e.target;

        if (name === "latitud y longitud") {
            // Separar la cadena en latitud y longitud
            const [latitud, longitud] = value.split(',').map(coord => coord.trim());

            // Actualizar el estado con los valores separados
            setFormData({
                ...formData,
                latitud: parseFloat(latitud), // Convertir a número
                longitud: parseFloat(longitud) // Convertir a número
            });
        } else {
            // Para otros campos, actualiza el estado normalmente
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit} className="gestor-formulario grid-form">
                <div className="form-group">
                    <label>Nombre</label>
                    <input type="text" name="title" value={formData.title} onChange={handleChange} required/>
                </div>
                <div className="form-group">
                    <label>Descripción</label>
                    <textarea name="description" value={formData.description} onChange={handleChange} required/>
                </div>

                {/** Mostrar la imagen del módulo **/}
                <div className="form-group">
                    <label>Imagen del Módulo</label>
                    {imgUrl ? (
                        <img
                            src={imgUrl}
                            alt="Preview módulo"
                            style={{width: "100px", height: "auto", marginBottom: "5px"}}
                        />
                    ) : (
                        <p>No hay imagen</p>
                    )}
                    <div>
                        <Button variant="info" onClick={handleVerImagen}>
                            Ver Imagen
                        </Button>{" "}
                        <Button variant="secondary" onClick={handleActualizarImagen}>
                            Actualizar Imagen
                        </Button>
                    </div>
                    <input
                        type="file"
                        style={{display: "none"}}
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept=".jpg,.jpeg,.png,.gif,.webp"
                    />
                </div>

                {/* Campos de ubicación */}
                <div className="form-group">
                    <label>Latitud y Longitud (Formato: latitud, longitud)</label>
                    <input type="text" name="latitud y longitud" onChange={handleCoordenadasChange} required/>
                </div>
                <div className="form-group">
                    <label>Dirección</label>
                    <input type="text" name="direccion" value={formData.direccion} onChange={handleChange} required/>
                </div>
                <div className="form-group">
                    <label>Estado</label>
                    <input type="text" name="estado" value={formData.estado} onChange={handleChange} required/>
                </div>
                <div className="form-group">
                    <label>Localidad</label>
                    <input type="text" name="localidad" value={formData.localidad} onChange={handleChange} required/>
                </div>

                {/** Dropdown para Tipo de Mueble **/}
                <div className="form-group">
                    <label>Tipo de Mueble</label>
                    <select name="tipo_mueble" value={formData.tipo_mueble} onChange={handleChange} required>
                        <option value="">Seleccione un tipo</option>
                        <option value="e1">Isla 1X1 Nueva</option>
                        <option value="e2">Isla 1.5 x 1.5</option>
                        <option value="e3">Demoset</option>
                        <option value="e4">Cilindro</option>
                        <option value="e5">Toreo</option>
                        <option value="e6">Isla 2x2m</option>
                        <option value="e6">Cabecera de Madera</option>
                        <option value="e6">Módulo Walmart</option>
                        <option value="e6">Local con Mesa Grapa</option>
                        <option value="e6">Isla Híbrido</option>
                        <option value="e6">Trebol</option>
                        <option value="e6">Torre Sky</option>
                    </select>
                </div>

                {/** Dropdown para Estado del Mueble **/}
                <div className="form-group">
                    <label>Estado del Mueble</label>
                    <select name="estado_mueble" value={formData.estado_mueble} onChange={handleChange} required>
                        <option value="">Seleccione un estado</option>
                        <option value="Nuevo">Nuevo</option>
                        <option value="buen estado">Buen estado</option>
                        <option value="regular">Regular</option>
                        <option value="mal estado">Mal estado</option>
                    </select>
                </div>

                {/** Dropdown para Imagen Viniles **/}
                <div className="form-group">
                    <label>Imagen Viniles</label>
                    <select name="imagen_viniles" value={formData.imagen_viniles} onChange={handleChange} required>
                        <option value="">Seleccione una opción</option>
                        <option value="vigentes">Vigentes</option>
                        <option value="vencidos">Vencidos</option>
                    </select>
                </div>

                <div className="form-group">
                    <label>Televisión</label>
                    <input type="text" name="television" value={formData.television} onChange={handleChange}/>
                </div>
                <div className="form-group checkbox-group">
                    <label>TV Funcional</label>
                    <input type="checkbox" name="tv_funcional" checked={boolValue(formData.tv_funcional)}
                           onChange={handleChange}/>
                </div>
                <div className="form-group checkbox-group">
                    <label>Conexión Luz</label>
                    <input type="checkbox" name="conexion_luz" checked={boolValue(formData.conexion_luz)}
                           onChange={handleChange}/>
                </div>
                <div className="form-group checkbox-group">
                    <label>Cuenta Staff</label>
                    <input type="checkbox" name="cuenta_staff" checked={boolValue(formData.cuenta_staff)}
                           onChange={handleChange}/>
                </div>
                <div className="form-group">
                    <label>Comentarios</label>
                    <textarea name="comentarios" value={formData.comentarios} onChange={handleChange}/>
                </div>

                {/* Botones Guardar / Cancelar */}
                <div className="form-buttons">
                    <button type="submit" className="btn-save">
                        Guardar
                    </button>
                    <button type="button" className="btn-cancel" onClick={onCancelar}>
                        Cancelar
                    </button>
                </div>
            </form>
        </>
    );
};

export default FormularioPuntoVenta;
